import type {UrlStateManagerService} from 'shared/state-manager';
import type {FinishedResponseInteractionService} from 'shared/services';
import {Subkind} from 'shared/constants';
import type {DepartmentResponse, FieldId, FieldTypeId, Fond, Rec, RecordEditation, RecordHolding, RecordStatusId, SetFieldValueRequest, SimpleFond, TypeOrIdentifiedStub, UUID} from 'src/typings';
import type {GridDeleteRequest, GridEditRequest, CellModificationResponse, GridFieldValue, GridRegenerateRequest, RecordRow} from '../lib/types';
import type {FondFieldTypeDefinitions, GridLayout} from 'src/features/record/field-types/types';
import type {RecordGridDataService} from './record-grid.data-service';
import type {RecordEditationDataService} from '../../record-editation/services/record-editation.data-service';
import type {LogService} from 'core/logging/log.service';
import type {FondsDataService} from '../../fonds/services/fonds.data-service';
import type RecordDataService from 'src/features/record/record.data-service';
import type {RecordHoldingDataService} from 'src/features/record/record-holding.data-service';
import type {FieldTypeDataService} from 'src/features/record/field-types/field-type-data.service';
import {createSetFieldValueRequest} from 'shared/utils/data-service-utils';
import {stringifyArgument} from 'shared/utils/error-utils';
import {hasTargetRecordReference} from 'src/features/record-grid/lib/types-utils';

export class RecordGridService {
    public static serviceName = 'recordGridService';

    /*@ngInject*/
    constructor(private recordGridDataService: RecordGridDataService,
                private fieldTypeDataService: FieldTypeDataService,
                private fondsDataService: FondsDataService,
                private recordEditationDataService: RecordEditationDataService,
                private recordDataService: RecordDataService,
                private recordHoldingDataService: RecordHoldingDataService,
                private logService: LogService,
                private urlStateManagerService: UrlStateManagerService<any>,
                private finishedResponseInteractionService: FinishedResponseInteractionService) {
    }

    public async getFond(fondId: number): Promise<Fond> {
        try {
            return await this.fondsDataService.getById(fondId);
        } catch (error) {
            this.logService.error(`Can not load fond by id: ${fondId}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async getIncludableFonds(fond: TypeOrIdentifiedStub<SimpleFond>): Promise<Fond[]> {
        try {
            return this.fondsDataService.getIncludableFonds(fond.id);
        } catch (error) {
            this.logService.error(`Error when loading compatible fonds for fond: ${fond?.id}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async getInsertableFonds(fond: TypeOrIdentifiedStub<SimpleFond>) {
        try {
            return await this.fondsDataService.getInsertableFonds(fond.id);
        } catch (error) {
            this.logService.error(`Error when loading insertable fonds for fond: ${fond?.id}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async loadFondFieldTypeDefinitions(fond: TypeOrIdentifiedStub<SimpleFond>): Promise<FondFieldTypeDefinitions> {
        try {
            return await this.fieldTypeDataService.loadFondFieldTypeDefinitions(fond.id);
        } catch (error) {
            this.logService.error(`Error when loading table field type definitions for fond: ${fond?.id}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async loadGridLayout(fond: TypeOrIdentifiedStub<SimpleFond>): Promise<GridLayout> {
        try {
            return await this.recordGridDataService.loadLayout(fond);
        } catch (error) {
            this.logService.error(`Error when loading table layout for fond: ${fond?.id}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async loadSingleRow(recordId: UUID): Promise<RecordRow> {
        try {
            return await this.recordGridDataService.loadSingleRow(recordId);
        } catch (error) {
            this.logService.error(`Error when loading record: ${recordId}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async loadRows(records: TypeOrIdentifiedStub<RecordRow>[]): Promise<RecordRow[]> {
        try {
            return await this.recordGridDataService.loadRows(records.map((record) => record.id));
        } catch (error) {
            this.logService.error(`Error when loading records: ${stringifyArgument(records)}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async createNewRow(fond: Fond, initialValues?: Record<FieldTypeId, SetFieldValueRequest>): Promise<RecordRow> {
        let recordEditation: RecordEditation;
        try {
            recordEditation = await this.recordEditationDataService.createNewEditation({
                subkind: fond.ofAuthority ? Subkind.SUBKIND_AUTHORITY : Subkind.SUBKIND_DOCUMENT,
                fond,
                rootFond: fond,
                initialValues
            });
        } catch (error) {
            this.logService.error('Error when creating new record', error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }

        return this.loadSingleRow(recordEditation.recordId);
    }

    public async editCell(recordId: UUID, fieldId: FieldId, value: GridFieldValue): Promise<CellModificationResponse> {
        return this.editCells(recordId, [{fieldId, value}]);
    }

    public async editCells(recordId: UUID, cells: {
        fieldId: FieldId,
        value: GridFieldValue
    }[]): Promise<CellModificationResponse> {
        try {
            const request: GridEditRequest = {
                cellEditRequests: cells.map(({fieldId, value}) => {
                    return {record: recordId, fieldId, setFieldValueRequest: this.createSetFieldValueRequest(value), additionalInvalidatingRecords: []};
                })
            };
            return await this.recordGridDataService.editCells(request);
        } catch (error) {
            this.logService.error(`Error when editing cells ${stringifyArgument(cells)} of record ${recordId}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }


    public async updateCells(updates: {row: RecordRow, fieldId: FieldId, value: GridFieldValue}[]): Promise<CellModificationResponse> {
        try {
            const request: GridEditRequest = {
                cellEditRequests: updates.map(({row, fieldId, value}) => ({record: row.id, fieldId, setFieldValueRequest: this.createSetFieldValueRequest(value), additionalInvalidatingRecords: []}))
            };
            return await this.recordGridDataService.editCells(request);
        } catch (error) {
            this.logService.error(`Error when making updates ${stringifyArgument(updates)}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async deleteCell(recordId: UUID, fieldId: FieldId): Promise<CellModificationResponse> {
        try {
            const request: GridDeleteRequest = {
                cellDeleteRequests: [{record: recordId, fieldId}]
            };
            return await this.recordGridDataService.deleteCells(request);
        } catch (error) {
            this.logService.error(`Error when deleting cell ${fieldId} of record ${recordId}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async deleteCells(updates: {row: RecordRow, fieldId: FieldId}[]): Promise<CellModificationResponse> {
        try {
            const request: GridDeleteRequest = {
                cellDeleteRequests: updates.map(({row, fieldId}) => ({record: row.id, fieldId}))
            };
            return await this.recordGridDataService.deleteCells(request);
        } catch (error) {
            this.logService.error(`Error when deleting cells ${stringifyArgument(updates)}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async regenerateCells(updates: {row: RecordRow, fieldId: FieldId}[]): Promise<CellModificationResponse> {
        try {
            const request: GridRegenerateRequest = {
                cellRegenerateRequests: updates.map(({row, fieldId}) => ({record: row.id, fieldId}))
            };
            return await this.recordGridDataService.regenerateCells(request);
        } catch (error) {
            this.logService.error(`Error when regenerating cells ${stringifyArgument(updates)}`, error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async duplicateRow(recordRow: RecordRow): Promise<RecordRow> {
        let record: Rec;
        try {
            const copyOptions = {
                targetFond: recordRow.fond,
                copyFiles: true,
                copyMetadata: true,
                publishCopiedRecord: false
            };
            const response = await this.recordDataService.copyRecord(recordRow, copyOptions);
            record = response.savedObject;
        } catch (error) {
            this.logService.error('Error when creating duplicating record', error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }

        return this.loadSingleRow(record.id);
    }

    public async deleteRow(recordRow: RecordRow) {
        try {
            await this.recordDataService.deleteSubtreeHoldings(recordRow);
        } catch (error) {
            this.logService.error('Error when deleting record', error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public async changeStatus(recordRow: RecordRow, recordStatusId: RecordStatusId) {
        try {
            const response = await this.recordDataService.changeStatus(recordRow, recordStatusId);
            return {newRow: await this.loadSingleRow(recordRow.id), modifiedRecordIds: response.modifiedRecordIds};
        } catch (error) {
            this.logService.error('Error when changing record status', error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    public resetUrlState() {
        this.urlStateManagerService.requestChangeState({});
    }

    public async createRecordHolding(recordRow: RecordRow, targetDepartment: DepartmentResponse): Promise<RecordHolding> {
        try {
            const response = await this.recordHoldingDataService.createRecordHolding(recordRow, targetDepartment);
            return response.savedObject;
        } catch (error) {
            this.logService.error('Error when creating record holding', error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }
    public async deleteRecordHolding(recordHolding: RecordHolding): Promise<void> {
        try {
            await this.recordHoldingDataService.deleteRecordHolding(recordHolding);
        } catch (error) {
            this.logService.error('Error when deleting record holding', error);
            this.finishedResponseInteractionService.showFailedResponseInToast(error);
            throw error;
        }
    }

    // because we set record link instead of actual value
    private createSetFieldValueRequest(value: GridFieldValue): SetFieldValueRequest | null {
        return createSetFieldValueRequest(hasTargetRecordReference(value) ? value.targetRecordReference : value);
    }
}
